// Reusable file-type detectors + labels + icon classes

// --- core getters ---
export function getFileMime(item) {
  const mime = (item?.fileMime || item?.mime || "").toLowerCase();
  return mime; // e.g. "application/pdf", "image/png"
}

export function getFileExt(input) {
  const url =
    typeof input === "string" ? input : input?.fileUrl || input?.url || "";
  if (!url) return "";
  const clean = url.split("?")[0].split("#")[0];
  const idx = clean.lastIndexOf(".");
  return idx >= 0 ? clean.substring(idx + 1).toLowerCase() : "";
}

// --- detectors ---
// All detectors accept either an object {fileUrl, fileMime} or {url, mime}
// or a plain URL string (mime will be empty in that case).
export function isImage(item) {
  const mime = getFileMime(item);
  const ext = getFileExt(item);
  if (mime) return mime.startsWith("image/");
  return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext);
}

export function isPdf(item) {
  const mime = getFileMime(item);
  const ext = getFileExt(item);
  if (mime) return mime === "application/pdf";
  return ext === "pdf";
}

export function isDoc(item) {
  const mime = getFileMime(item);
  const ext = getFileExt(item);
  if (mime) return mime.includes("word") || mime === "application/msword";
  return ["doc", "docx", "rtf", "odt"].includes(ext);
}

export function isSheet(item) {
  const mime = getFileMime(item);
  const ext = getFileExt(item);
  if (mime) return mime.includes("sheet") || mime.includes("excel");
  return ["xls", "xlsx", "ods", "csv"].includes(ext);
}

export function isSlide(item) {
  const mime = getFileMime(item);
  const ext = getFileExt(item);
  if (mime) return mime.includes("presentation") || mime.includes("powerpoint");
  return ["ppt", "pptx", "odp"].includes(ext);
}

export function isVideo(item) {
  const mime = getFileMime(item);
  const ext = getFileExt(item);
  if (mime) return mime.startsWith("video/");
  return ["mp4", "webm", "ogg", "mov", "m4v", "avi", "mkv"].includes(ext);
}

export function isAudio(item) {
  const mime = getFileMime(item);
  const ext = getFileExt(item);
  if (mime) return mime.startsWith("audio/");
  return ["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(ext);
}

export function isArchive(item) {
  const ext = getFileExt(item);
  return ["zip", "rar", "7z", "tar", "gz", "bz2"].includes(ext);
}

export function isText(item) {
  const mime = getFileMime(item);
  const ext = getFileExt(item);
  if (mime) return mime.startsWith("text/") || mime === "application/json";
  return ["txt", "md", "log", "json", "xml", "yaml", "yml", "csv"].includes(
    ext
  );
}

// --- label ---
export function fileLabel(item) {
  if (isPdf(item)) return "Tệp PDF";
  if (isDoc(item)) return "Tài liệu Word";
  if (isSheet(item)) return "Bảng tính";
  if (isSlide(item)) return "Trình chiếu";
  if (isVideo(item)) return "Video";
  if (isAudio(item)) return "Audio";
  if (isArchive(item)) return "Tệp nén";
  if (isText(item)) return "Tệp văn bản";
  if (isImage(item)) return "Hình ảnh";
  return "Tệp đính kèm";
}

// --- icon (Font Awesome v4/v5+) ---
// opts.fa: 4 | 5 (mặc định 5+). Với FA4, các icon có hậu tố -o.
export function fileIconClass(item, opts = {}) {
  const fa = Number(opts.fa) === 4 ? 4 : 5;
  const base = "fa"; // bạn đang dùng 'fa ...' (Font Awesome 4 style). Nếu dùng v5+, vẫn hoạt động do alias.

  if (isPdf(item))
    return `${base} ${fa === 4 ? "fa-file-pdf-o" : "fa-file-pdf"}`;
  if (isDoc(item))
    return `${base} ${fa === 4 ? "fa-file-word-o" : "fa-file-word"}`;
  if (isSheet(item))
    return `${base} ${fa === 4 ? "fa-file-excel-o" : "fa-file-excel"}`;
  if (isSlide(item))
    return `${base} ${
      fa === 4 ? "fa-file-powerpoint-o" : "fa-file-powerpoint"
    }`;
  if (isVideo(item))
    return `${base} ${fa === 4 ? "fa-file-video-o" : "fa-file-video"}`;
  if (isAudio(item))
    return `${base} ${fa === 4 ? "fa-file-audio-o" : "fa-file-audio"}`;
  if (isArchive(item))
    return `${base} ${fa === 4 ? "fa-file-archive-o" : "fa-file-archive"}`;
  if (isText(item))
    return `${base} ${fa === 4 ? "fa-file-text-o" : "fa-file-lines"}`;
  if (isImage(item))
    return `${base} ${fa === 4 ? "fa-file-image-o" : "fa-file-image"}`;
  return `${base} ${fa === 4 ? "fa-file-o" : "fa-file"}`;
}

// --- convenient open helper ---
export function openInNewTab(url) {
  if (!url) return;
  window.open(url, "_blank", "noopener");
}

// --- default export (optional) ---
export default {
  getFileMime,
  getFileExt,
  isImage,
  isPdf,
  isDoc,
  isSheet,
  isSlide,
  isVideo,
  isAudio,
  isArchive,
  isText,
  fileLabel,
  fileIconClass,
  openInNewTab,
};
